import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiKey } from '../interceptors/base-url.interceptor';
import { SKIP_AUTH } from '../tokens/http-context.tokens';

export interface RuntimeEnv {
  environment?: string;
  configFile?: string;
  googleGTM?: string;
}

@Injectable({ providedIn: 'root' })
export class ConfigLoaderService {
  private config: any | null = null;

  constructor(private http: HttpClient) {}

  // Called by APP_INITIALIZER. Should resolve only when config is loaded (or fail gracefully).
  async load(): Promise<void> {
    // 等待最多 3 秒讓 env.js 載入
    let attempts = 0;
    const maxAttempts = 60; // 3秒 / 50ms = 60次

    // 確保 env.js 已經載入完成
    while (!(window as any)?.env && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 50));
      attempts++;
    }
    const env: RuntimeEnv | undefined = (window as any)?.env;
    const configUrl = env?.configFile ?? '/configs/config.json';

    try {
      // 跳過認證攔截器
      this.config = await firstValueFrom(
        this.http.get<any>(configUrl, {
          context: new HttpContext().set(SKIP_AUTH, true)
        })
      );

      // 載入 Google GTM
      const gtmId = this.config?.googleGTM;
      if (gtmId) {
        this.loadGoogleTagManager(gtmId);
      }

      // Optional: log which environment was loaded
      if (env?.environment) {
        console.log('[ConfigLoader] Environment:', env.environment, 'from', configUrl);
        if (gtmId) {
          console.log('[ConfigLoader] Google GTM loaded:', gtmId);
        }
      } else {
        console.log('[ConfigLoader] Loaded config from', configUrl);
      }
    } catch (error) {
      console.error('[ConfigLoader] Failed to load config from', configUrl, error);
      // Decide whether to reject or resolve to allow the app to continue.
      // Here we resolve to avoid blocking bootstrap; adjust to your preference.
    }
  }

  /**
   * 動態載入 Google Tag Manager
   * @param gtmId Google Tag Manager ID (例如: GTM-XXXXXXX)
   */
  private loadGoogleTagManager(gtmId: string): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return; // SSR 環境不執行
    }

    // 初始化 dataLayer
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    // 插入 GTM script 到 head
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.insertBefore(script, document.head.firstChild);

    // 插入 GTM noscript 到 body
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);

    // 等待 body 載入完成後插入 noscript
    if (document.body) {
      document.body.insertBefore(noscript, document.body.firstChild);
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        document.body.insertBefore(noscript, document.body.firstChild);
      });
    }
  }

  getConfig<T = any>(): T | null {
    return this.config as T | null;
  }

  getApiBaseUrls(): Partial<Record<ApiKey, string>> | null {
    return this.config?.API_BASE_URL || null;
  }

  getGoogleGTM(): string | null {
    return this.config?.googleGTM || null;
  }
}

